/**
 * StatusVFXHandler — persistent status/buff aura presentation, extracted
 * from VFXSystem (decomposition arc, 2026-06-12).
 *
 *  - `vfx:status_effect`        → server + local prediction (StatusEffectBridge
 *                                 feeds StatusEffectManager for every viewer)
 *  - `vfx:mana_shield`          → dedicated persistent shield effect
 *  - EVENTS.WIRE_CONSUMABLE_USED → one-shot consumable use puff
 *  - EVENTS.PLAYER_HEALED        → the heal's authored landing on the healed
 *                                 player (HealLandingVFX, plan 29 F9)
 *
 * Auras spawn at the target's CURRENT position and stay attached via the
 * FollowerSystem; instance ids are `${targetId}_${effectId}` so apply/remove
 * pair up across origins.
 */
import { EVENTS } from '../../../shared/EventBus';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import type { VFXEffectPlayer } from '../VFXEffectPlayer';
import { VFXEffectRegistry } from '../VFXEffectRegistry';
import { getAbilityVFX } from '../config/AbilityVFXMap';
import { STATUS_AURA_CONFIG } from '../../../config/abilities/statusAura.config';
import { findStatusTargetPosition, findPlayerPosition } from './entityPositions';
import { HealLandingVFX } from './HealLandingVFX';
import { resolveConsumableUseVfx } from '../config/abilities/ConsumableVFX';
import { resolveStatusPresentation } from '../config/statusPresentation.config';
import { PendingStatusVFX, PENDING_STATUS_LIMIT, type StatusVFXEvent } from './PendingStatusVFX';
import { statusWorldPlacement } from './statusWorldPlacement';

const statusKey = (targetId: string, effectId: string) => JSON.stringify([targetId, effectId]);
const statusExpiry = (duration: number | undefined, now = Date.now()) =>
  typeof duration === 'number' && Number.isFinite(duration) ? now + Math.max(0, duration) : Infinity;

export class StatusVFXHandler {
  // Plan 29 F9 (lane CA): a server heal lands on whoever it healed, every screen.
  private healLanding: HealLandingVFX;
  private readonly pending = new PendingStatusVFX();
  private readonly statuses = new Map<string, { data: StatusVFXEvent; expiresAt: number }>();

  constructor(private effectPlayer: VFXEffectPlayer) {
    this.healLanding = new HealLandingVFX({
      playerPosition: (sid) => findPlayerPosition(sid),
      play: (effectId, x, y, overrides) => void this.effectPlayer.playById(effectId, x, y, overrides),
    });
  }

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub('vfx:status_effect', (data) => this.onStatusEffect(data));
    sub('vfx:status_stack_update', (data) => this.onStatusRefresh(data));
    sub('vfx:mana_shield', (data) => this.onManaShield(data));
    sub(EVENTS.WIRE_CONSUMABLE_USED, (data) => this.onConsumableUsed(data));
    sub(EVENTS.PLAYER_HEALED, (data) => this.healLanding.onHeal(data));
    sub(EVENTS.NET_REMOTE_PLAYER_LEAVE, (data) => this.forget(data.sessionId));
    sub(EVENTS.NET_NPC_LEAVE, (data) => this.forget(data.npcId ?? data.id));
    sub(EVENTS.NET_DISCONNECTED, () => this.teardown());
    sub(EVENTS.MAP_SERVER_SWITCH_START, () => this.teardown());
  }

  update(): void {
    const now = Date.now();
    for (const [key, entry] of this.statuses) {
      if (entry.expiresAt <= now) {
        this.statuses.delete(key);
        this.pending.remove(entry.data.targetId, entry.data.effectId);
      }
    }
    this.pending.update((data) => this.presentStatus(data), now);
  }
  teardown(): void {
    for (const entry of this.statuses.values()) {
      if (statusWorldPlacement(entry.data) !== undefined) {
        this.effectPlayer.stopPersistentEffect(`${entry.data.targetId}_${entry.data.effectId}`);
      }
    }
    this.pending.clear(); this.statuses.clear();
  }

  private forget(targetId: string): void {
    this.pending.forget(targetId);
    for (const [key, entry] of this.statuses) {
      if (entry.data.targetId !== targetId) continue;
      this.statuses.delete(key);
      this.effectPlayer.stopPersistentEffect(`${targetId}_${entry.data.effectId}`);
    }
  }

  private onStatusRefresh(data: Omit<StatusVFXEvent, 'active'>): void {
    const entry = this.statuses.get(statusKey(data.targetId, data.effectId));
    // A refresh may extend an owned status, never resurrect a removed,
    // expired, evicted, or previous-scene identity.
    if (!entry || entry.expiresAt <= Date.now()) return;
    const abilityId = typeof data.data?.abilityId === 'string' ? data.data.abilityId : data.abilityId ?? entry.data.abilityId;
    const duration = data.duration ?? (Number.isFinite(entry.expiresAt) ? entry.expiresAt - Date.now() : undefined);
    this.onStatusEffect({ ...entry.data, ...data, active: true, duration, ...(abilityId ? { abilityId } : {}) });
  }

  private onStatusEffect(data: StatusVFXEvent): void {
    const instanceId = `${data.targetId}_${data.effectId}`;
    const key = statusKey(data.targetId, data.effectId);
    this.pending.remove(data.targetId, data.effectId);
    this.statuses.delete(key);
    VisualLifecycleStore.record({
      type: 'status_vfx',
      label: `${data.effectId} ${data.active ? 'ON' : 'off'} → ${data.targetId}`,
      x: 0, y: 0,
      abilityId: data.abilityId ?? null,
      targetId: data.targetId,
      meta: { active: data.active },
    });
    if (data.active) {
      // Bound both waiting and rendered identity bookkeeping. Evicted owners
      // stop their visual, so a later stack-only event cannot revive them.
      if (this.statuses.size >= PENDING_STATUS_LIMIT) {
        const oldest = this.statuses.entries().next().value!;
        this.statuses.delete(oldest[0]);
        this.pending.remove(oldest[1].data.targetId, oldest[1].data.effectId);
        this.effectPlayer.stopPersistentEffect(`${oldest[1].data.targetId}_${oldest[1].data.effectId}`);
      }
      this.statuses.set(key, { data: { ...data }, expiresAt: statusExpiry(data.duration) });
      if (!this.presentStatus(data)) this.pending.add(data);
    } else {
      this.effectPlayer.stopPersistentEffect(instanceId);
    }
  }

  /** Missing bodies or exhausted shape budgets defer without restarting the lifetime. */
  private presentStatus(data: StatusVFXEvent): boolean {
    const world = statusWorldPlacement(data);
    if (world === null) return false;
    const pos = world ?? findStatusTargetPosition(data.targetId);
    if (!pos) return false;
    const instanceId = `${data.targetId}_${data.effectId}`;
    const statusStyle = !VFXEffectRegistry.has(data.effectId) ? STATUS_AURA_CONFIG[data.effectId] : undefined;
    const overrides: NonNullable<Parameters<VFXEffectPlayer['playPersistent']>[4]> =
      statusStyle && data.color === undefined ? { ...data, color: statusStyle.color } : { ...data };
    if (world) {
      // Status identity still owns removal; it does not become a body follower.
      delete overrides.targetId;
      overrides.follow = false; overrides.areaRadius = world.radius;
    }
    const presentationId = resolveStatusPresentation(data, (id) => VFXEffectRegistry.has(id));
    const effectId = presentationId ?? this.resolveStatusEffectId(data);
    const definition = VFXEffectRegistry.get(effectId);
    // Retry only pure shape definitions: replaying a mixed aura would repeat
    // smoke bursts, entity-override timers or ribbon attachments on each miss.
    const pureShapes = !!definition?.primitives.length && definition.primitives.every(primitive =>
      primitive.type !== 'particles' && primitive.type !== 'entity_override' && primitive.type !== 'ribbon')
      && !definition.screenShake && !definition.screenFlash && !definition.screenTint
      && !definition.floatingText && !definition.animatedText;
    const authoredMinimum = definition?.minimumShapes;
    const minimum = pureShapes
      ? (typeof authoredMinimum === 'number' && Number.isFinite(authoredMinimum) && authoredMinimum > 0 ? Math.ceil(authoredMinimum) : 1)
      : (!definition ? 1 : 0);
    // Incoming identity owns replacement and teardown even when the rendered def is mapped.
    if (presentationId) this.effectPlayer.stopPersistentEffect(instanceId);
    const shapes = this.effectPlayer.playPersistent(instanceId, effectId, pos.x, pos.y, overrides);
    if (shapes.length < minimum) {
      // A one-sided partial pair must not occupy the last slot forever. The
      // existing bounded queue retries with the original deadline's remainder.
      this.effectPlayer.stopPersistentEffect(instanceId);
      return false;
    }
    return true;
  }

  private onConsumableUsed(data: {
    targetId?: string;
    itemId?: string;
    effectIds?: string[];
  }): void {
    if (!data?.targetId || !data.itemId) return;
    const effectId = resolveConsumableUseVfx(
      Array.isArray(data.effectIds) ? data.effectIds : [],
    );
    const pos = findStatusTargetPosition(data.targetId);
    VisualLifecycleStore.record({
      type: 'status_vfx',
      label: `${data.itemId} → ${effectId}`,
      x: pos?.x ?? 0,
      y: pos?.y ?? 0,
      targetId: data.targetId,
      meta: { itemId: data.itemId, effectId },
    });
    this.effectPlayer.playById(effectId, pos?.x ?? 0, pos?.y ?? 0, {
      targetId: data.targetId,
      itemId: data.itemId,
    });
  }

  /**
   * Resolve a status-effect id against the registry with an abilityId
   * fallback (mirrors spawnEffectFromData): raw id → the ability's
   * registered `${abilityId}_aura` (persistent — the right category for a
   * duration-bound status) → the ability's mapped cast VFX. Behaviors
   * resolve before emitting, so this only catches emitters that pass a raw
   * unregistered id alongside an abilityId.
   */
  private resolveStatusEffectId(data: { effectId: string; abilityId?: string }): string {
    if (VFXEffectRegistry.has(data.effectId)) return data.effectId;
    // Plain status ids (burn/poison/slow/frozen…) → tinted base aura. These
    // were never registered effects, so every status aura silently resolved
    // to nothing on every client until 2026-06-12. A future bespoke def for
    // the raw id automatically wins (registry check above).
    const statusStyle = STATUS_AURA_CONFIG[data.effectId];
    if (statusStyle && VFXEffectRegistry.has(statusStyle.effectId)) return statusStyle.effectId;
    if (!data.abilityId) return data.effectId;
    const auraId = `${data.abilityId}_aura`;
    if (VFXEffectRegistry.has(auraId)) return auraId;
    const mapping = getAbilityVFX(data.abilityId);
    if (mapping?.castVfxId && VFXEffectRegistry.has(mapping.castVfxId)) return mapping.castVfxId;
    return data.effectId;
  }

  private onManaShield(data: { targetId: string, active: boolean, duration?: number }): void {
    this.onStatusEffect({ ...data, effectId: 'mana_shield' });
  }
}
